import { useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";

const TestimonialSlider = () => {
  const [startIndex, setStartIndex] = useState(0);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  
  // Fetch testimonials from the API
  const { data: testimonials, isLoading, error } = useQuery({
    queryKey: ["testimonials"],
    queryFn: async () => {
      const response = await fetch("https://dash.brandexperts.ae/dash/testimonials/");
      if (!response.ok) {
        throw new Error("Failed to fetch testimonials");
      }
      return response.json();
    }
  });
  
  // Fallback to empty array if data is loading
  const testimonialData = testimonials || [];

  const slideLeft = () => {
    setStartIndex((prev) => (prev === 0 ? testimonialData.length - 1 : prev - 1));
  };

  const slideRight = () => {
    setStartIndex((prev) => (prev === testimonialData.length - 1 ? 0 : prev + 1));
  };

  const openModal = (testimonial) => {
    setSelectedTestimonial(testimonial);
    // Prevent body scrolling when modal is open
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedTestimonial(null);
    // Re-enable body scrolling
    document.body.style.overflow = 'auto';
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const headerVariants = {
    hidden: { y: -50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const cardVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { type: "spring", stiffness: 300, damping: 25 }
    },
    exit: { 
      opacity: 0, 
      scale: 0.8,
      transition: { duration: 0.2 }
    }
  };

  // Show loading state when testimonials are being fetched
  if (isLoading) {
    return (
      <div className="w-full bg-[#fdf5f5] py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p>Loading testimonials...</p>
        </div>
      </div>
    );
  }

  // Show error state if there was an issue fetching testimonials
  if (error) {
    return (
      <div className="w-full bg-[#fdf5f5] py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p>Error loading testimonials. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      variants={containerVariants}
      className="w-full bg-[#fdf5f5] py-16"
    >
      <div className="max-w-7xl mx-auto px-4">
        <motion.div 
          className="text-center mb-8"
          variants={headerVariants}
        >
          <h2 className="text-3xl font-bold">what our client say about</h2>
          <h3 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-[#BF1A1C] to-[#a01618]">
            Brand Experts
          </h3>
        </motion.div>

        <div className="relative">
          <div className="flex justify-end gap-2 mb-6 px-4">
            <button
              onClick={slideLeft}
              className="w-10 h-10 border-2 border-red-600 text-red-600 rounded-md flex items-center justify-center hover:bg-red-700 hover:text-white transition-colors cursor-pointer"
              aria-label="Previous testimonial"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={slideRight}
              className="w-10 h-10 border-2 border-red-600 text-red-600 rounded-md flex items-center justify-center hover:bg-red-700 hover:text-white transition-colors cursor-pointer"
              aria-label="Next testimonial"
            >
              <ChevronRight size={24} />
            </button>
          </div>

          <motion.div className="overflow-hidden">
            <motion.div 
              className="flex gap-6"
              style={{
                transform: `translateX(-${startIndex * 33.33}%)`,
              }}
              transition={{ type: "spring", stiffness: 150, damping: 20 }}
            >
              {testimonialData.length > 0 && [...testimonialData, ...testimonialData].map((testimonial, index) => (
                <motion.div
                  key={index}
                  variants={cardVariants}
                  className="min-w-[300px] w-[300px] h-[300px] bg-white rounded-lg p-6 shadow-md flex flex-col cursor-pointer"
                  whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                  onClick={() => openModal(testimonial)}
                >
                  <div className="flex items-center gap-4 mb-4">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-red-600"
                    />
                    <div>
                      <h4 className="font-bold">{testimonial.name}</h4>
                      <p className="text-gray-600 text-sm">{testimonial.occupation}</p>
                    </div>
                  </div>
                  
                  <div className="mb-4 flex-grow overflow-hidden">
                    <p className="text-gray-700 text-sm line-clamp-4">
                      {testimonial.description}
                    </p>
                    {testimonial.description.length > 150 && (
                      <p className="text-red-600 text-xs mt-1 font-medium">Click to read more</p>
                    )}
                  </div>
                  
                  <div className="flex items-center justify-between mt-auto">
                    <div className="flex gap-1">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <span key={i} className="text-yellow-400 text-sm">⭐</span>
                      ))}
                    </div>
                    <p className="text-gray-400 text-xs">
                      {new Date(testimonial.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        <motion.div 
          className="text-center mt-12"
          variants={headerVariants}
        >
          <p className="text-lg mb-4">Join thousands of satisfied customers. Create your custom sign today!</p>
          <motion.button 
            className="bg-gradient-to-b from-[#BF1A1C] to-[#a01618] text-white px-8 py-3 rounded-lg font-bold"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Start Designing →
          </motion.button>
        </motion.div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {selectedTestimonial && (
          <div className="fixed inset-0  bg-opacity-60 flex items-center justify-center z-50 p-4" style={{ backgroundColor: "rgba(0, 0, 0, 0.60)" }} >
            <motion.div 
              className="bg-white rounded-xl max-w-lg w-full p-8 relative"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <button 
                onClick={closeModal}
                className="absolute top-4 right-4 text-gray-500 hover:text-red-600 transition-colors"
                aria-label="Close modal"
              >
                <X size={24} />
              </button>
              
              <div className="flex items-center gap-4 mb-6">
                <img
                  src={selectedTestimonial.image}
                  alt={selectedTestimonial.name}
                  className="w-20 h-20 rounded-full object-cover border-2 border-red-600"
                />
                <div>
                  <h3 className="text-xl font-bold">{selectedTestimonial.name}</h3>
                  <p className="text-gray-600">{selectedTestimonial.occupation}</p>
                  <div className="flex gap-1 mt-1">
                    {[...Array(selectedTestimonial.rating)].map((_, i) => (
                      <span key={i} className="text-yellow-400">⭐</span>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="max-h-64 overflow-y-auto mb-6 pr-2">
                <p className="text-gray-700 leading-relaxed">{selectedTestimonial.description}</p>
              </div>
              
              <div className="flex justify-between items-center border-t pt-4">
                <p className="text-gray-500 text-sm">
                  {new Date(selectedTestimonial.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
                <motion.button 
                  onClick={closeModal}
                  className="bg-gradient-to-b from-[#BF1A1C] to-[#a01618] text-white px-4 py-2 rounded-md text-sm font-medium"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Close
                </motion.button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default TestimonialSlider;